import React, { createContext, useContext, useEffect, useState } from 'react';

// Design variants. C is the live site. B stays reachable for review with ?v=b, but only on the page
// that carries it: nothing is remembered and nothing is added to the address bar. A was dropped on 20 Oct 2026.
export type VariantKey = 'b' | 'c';

export const VARIANTS: Record<VariantKey, { name: string; note: string }> = {
  b: { name: 'B · Product-led', note: 'Ramp-style, conversion-first, live product UI' },
  c: { name: 'C · Life of a unit', note: 'Scroll story: one machine from factory to every owner' },
};

const DEFAULT: VariantKey = 'c';

function variantFromUrl(): VariantKey {
  if (typeof window === 'undefined') return DEFAULT;
  const v = new URLSearchParams(window.location.search).get('v');
  return v && v in VARIANTS ? (v as VariantKey) : DEFAULT;
}

const VariantContext = createContext<{ variant: VariantKey; setVariant: (v: VariantKey) => void }>({
  variant: DEFAULT,
  setVariant: () => {},
});

export function VariantProvider({ children }: { children: React.ReactNode }) {
  const [variant, setVariant] = useState<VariantKey>(variantFromUrl);

  // Old links carry ?v=c (the default): drop it so shared addresses stay clean.
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get('v') !== DEFAULT) return;
    url.searchParams.delete('v');
    window.history.replaceState(window.history.state, '', url);
  }, []);

  return <VariantContext.Provider value={{ variant, setVariant }}>{children}</VariantContext.Provider>;
}

export const useVariant = () => useContext(VariantContext);

/** Review-only surfaces show on localhost / LAN, or anywhere with ?review=1. */
export function isReviewHost(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1' || host.startsWith('192.168.') || new URLSearchParams(window.location.search).has('review');
}
